import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { currencyFormatter } from '../../../utils/currencyFormat';
import DocumentActions from '../../common/DocumentActions';
import CustomPagination from '../../../components/customPagination';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { usePdfActions } from '../../common/DocumentPdf';
import { formatDate } from '../../../utils/formatDate';
import { useGetCreditNotesHandler } from '../dataHandlers/getCreditNotesHandler';
import DocumentTableStatusChip from '../../common/DocumentTableStatusChip';
import { useSelector } from 'react-redux';
import { B2C_ELECTRONIC_ADDRESS } from '../../../constants/appConstants';
import { useNavigate } from 'react-router-dom';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { OverallStatus } from '../../../utils/derivePeppolStatus';

const CreditNoteTable = ({ handlePaginationChange, searchQuery = {}, isPurchase = false }) => {
  const { downloadPdf } = usePdfActions();
  const subscription = useSelector((state) => state.subscription.subscription);
  const { creditNotes, totalDocuments, isLoading } = useGetCreditNotesHandler({ searchQuery, isPurchase });
  const isPhase2Enabled = subscription?.features?.enable_phase2;
  const navigate = useNavigate();
  const { eInvoiceForm } = useTranslatedMessages();
  const { document_tables } = eInvoiceForm;
  const tCredit = document_tables.credit_note;
  const tCommon = document_tables.common;
  const columns = [
    ...(isPhase2Enabled
      ? [
          {
            headerName: tCredit.status,
            field: 'active_status',
            flex: 0.8,
            renderCell: (params) => {
              if (!params.row.is_phase2_document) {
                return <Box justifyContent={'center'}>{tCommon.empty_status}</Box>;
              }
              return (
                <DocumentTableStatusChip status={params?.row?.meta_info?.peppol_status?.overall_status || 'PENDING'} />
              );
            },
          },
        ]
      : []),
    {
      headerName: tCredit.credit_note_no,
      field: 'document_number',
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="span"
          variant="span"
          onClick={() =>
            navigate(
              isPurchase
                ? `/purchases/documents/credit-notes/${params.row.id}`
                : `/sales/documents/credit-notes/${params.row.id}`,
            )
          }
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: 'none',
            },
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      headerName: tCredit.invoice_no,
      field: 'billing_reference',
      valueGetter: (params) => {
        if (!params || params.length === 0) return '-';
        return params.map((ref) => ref.id).join(', ');
      },
      flex: 1,
    },
    ...(isPurchase
      ? [
          {
            headerName: tCredit.source,
            field: 'document_source',
            flex: 1,
          },
        ]
      : []),
    {
      headerName: tCredit.customer_info,
      field: 'accounting_customer_party',
      flex: 1,
      valueGetter: (params) => {
        if (!params) return tCommon.not_available;
        return params.name;
      },
    },
    {
      headerName: tCredit.credit_note_date,
      field: 'issue_date',
      flex: 1,
      valueGetter: (params) => {
        return formatDate(params);
      },
    },
    {
      headerName: tCredit.amount,
      field: 'payable_amount',
      flex: 1,
      renderCell: ({ row }) =>
        row.document_currency_code === 'AED'
          ? `AED ${currencyFormatter().format(row.payable_amount)}`
          : currencyFormatter(row.document_currency_code).format(row.payable_amount),
    },
    {
      field: 'actions',
      headerName: tCredit.actions,
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        let allowPDFDownload = true;
        let allowResubmit = false;
        if (params.row.is_phase2_document) {
          const overallStatus = params?.row?.meta_info?.peppol_status?.overall_status;
          allowPDFDownload = overallStatus === OverallStatus.APPROVED;
          allowResubmit =
            (overallStatus === OverallStatus.REJECTED || overallStatus === OverallStatus.VALIDATION_FAILED) &&
            params.row.document_status !== 'OBSOLETE';
        }

        return (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: 0.6,
            }}
          >
            <>
              <Tooltip title={tCommon.download_document}>
                <IconButton
                  size="small"
                  onClick={() =>
                    downloadPdf({
                      url: `${isPurchase ? 'purchase' : 'sales'}-credit-notes/${params.row.id}/download-pdf`,
                      fileName: `CN-${params.row.id}.pdf`,
                    })
                  }
                  data-testid="download-pdf-button"
                  disabled={!allowPDFDownload}
                  sx={{
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  }}
                >
                  <PictureAsPdfIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </>
            <DocumentActions
              documentId={params?.row?.id}
              documentType={'credit-note'}
              documentApiBaseKey={'credit-notes'}
              isPurchase={isPurchase}
              allowIssueCreditNote={false}
              allowResubmit={allowResubmit}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={creditNotes}
        columns={columns}
        getRowId={(row) => row.id}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default CreditNoteTable;
